import { AxiosResponse } from 'axios'
import useSWR from 'swr'

import { FormAPI, TargetingAPI } from 'api'
import { getPlaybookCampaignId } from 'utils/playbooks'
import { trackEvent } from 'utils/tracking'

import { createPlaybook } from './playbooks'

export const FastlaneFormTypes = {
  BASIC: 'BASIC',
  MARKETO: 'MARKETO',
  HUBSPOT: 'HUBSPOT',
  PARDOT: 'PARDOT',
  ELOQUA: 'ELOQUA',
  UNKNOWN: 'UNKNOWN',
  /** None represents the choice users have to create Manual FL Forms without a form in setup */
  NONE: 'NONE',
} as const

export type FastlaneFormTypeLiteral =
  typeof FastlaneFormTypes[keyof typeof FastlaneFormTypes]

interface FormAPIFormField {
  name: string
  type: string
  label: string
  id: string
  placeholder?: string
  hidden?: boolean
}

export interface FLFormAPIForm {
  id?: string
  quickSetupCompatible: boolean
  type: FastlaneFormTypeLiteral
  fields: FormAPIFormField[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const detectForms = <T extends AxiosResponse<any>>(url: string) =>
  FormAPI.get('/forms/detect', { params: { url } }) as Promise<T>

type DetectFormsResponse = AxiosResponse<{ forms: FLFormAPIForm[] }>

export const useDetectForm = (
  formUrl: string,
  params?: {
    onSuccess: (data: DetectFormsResponse) => void
  }
) =>
  useSWR<DetectFormsResponse>(
    formUrl ? ['detectForms', formUrl] : null,
    () => detectForms<DetectFormsResponse>(formUrl),
    { onSuccess: params?.onSuccess }
  )

const convertFormFieldsToMapEntries = (fields: FormAPIFormField[]) =>
  fields.map((field) => ({
    formField: field.name,
    displayName: field.label || field.placeholder,
    fieldType: field.type,
    autoDetected: true,
  }))

/**
 * API for saving fastlane settings including form mappings, Fastlane Quick Setup (2023)
 */
export const saveFastlaneSettings = ({
  campaignId,
  formUrl,
  followUpUrl,
  form,
}: {
  campaignId: number
  formUrl: string
  followUpUrl: string
  form: FLFormAPIForm
}) => {
  let fastlaneSettings = {
    formType: form?.type ? form.type : 'BASIC',
    formUrl,
    formName: form?.id,
    formId: form?.id,
    redirectUri: followUpUrl,
    mapping: {
      mappingEntries: form.fields
        ? convertFormFieldsToMapEntries(form.fields)
        : [],
    },
  }
  return TargetingAPI.post(
    `/fastlane/v2/settings/${campaignId}`,
    fastlaneSettings
  )
}

/**
 * API for saving form mappings, Fastlane v.1 (2021)
 */
export const saveFormMapping = ({
  campaignId,
  formUrl,
  form,
}: {
  campaignId: number
  formUrl: string
  form: FLFormAPIForm
}) => {
  const formElementId = form.id
  const mappingEntries = form.fields
    ? convertFormFieldsToMapEntries(form.fields)
    : []

  return TargetingAPI.post(`/fastlane/form/${campaignId}`, {
    formUrl,
    formElementId,
    mappingEntries,
  })
}

// #region Shared Types

interface TargetingCondition {
  field: string
  operator: string
  value: string[]
  providerName: string | null
}

const SiteTriggerInjectionTypes = {
  FASTLANE: 'FASTLANE',
} as const

type SiteTriggerInjectionTypeLiteral =
  typeof SiteTriggerInjectionTypes[keyof typeof SiteTriggerInjectionTypes]

interface SiteTriggerInjection {
  id: number
  orgId: number
  siteTriggerType: SiteTriggerInjectionTypeLiteral
  targetingConditions: TargetingCondition[][]
}

interface FormField {
  id: number
  orgId: number
  fastlaneSettingId: number
  formFieldName: string
  formHtmlId: string
  fieldHtmlType: string
  displayOrder: number
  hidden: boolean
}

export interface FormFieldMapping {
  driftAttributes: string[]
  formFieldId: number
  campaignId: number
  orgId: number
}

export const FastlaneSettingsType = {
  MANUAL: 'NONE',
  QUICK_SETUP: 'QUICK_SETUP',
} as const

export type FastlaneSettingsTypeLiteral =
  typeof FastlaneSettingsType[keyof typeof FastlaneSettingsType]

interface FastlaneSettings {
  id: number
  siteTriggerInjectionId: number
  orgId: number
  campaignId: number
  formType: string
  formHtmlId: string
  detectedFormUrl: string
  deferredFormSubmission: boolean
  fastlaneSettingsType: FastlaneSettingsTypeLiteral
  fastlaneSettingsInitialType: FastlaneSettingsTypeLiteral
  redirectUrl: string
}

interface FLSetting {
  siteTriggerInjection?: SiteTriggerInjection
  formFields: FormField[]
  formFieldMappings: FormFieldMapping[]
  fastlaneSettings: FastlaneSettings
}

// #endregion

// #region Get Fastlane Settings for Playbook

export type FetchFLSettingResponse = FLSetting

const fetchFLSettings = (campaignId: number) =>
  TargetingAPI.get<FetchFLSettingResponse>(
    `/fastlane/v2/settings/config/${campaignId}`
  ).then((res) => res.data)

export const FetchFLSettingsQuery = {
  queryKey: (campaignId: number) => ['FetchFLSettings', `${campaignId}`],
  queryFn: fetchFLSettings,
} as const

// #endregion

// #region Create Fastlane Settings for Playbook

type SiteTriggerInjectionParams = Omit<SiteTriggerInjection, 'id' | 'orgId'>

type FastlaneSettingsParams = Omit<
  FastlaneSettings,
  'id' | 'orgId' | 'siteTriggerInjectionId' | 'fastlaneSettingsInitialType'
>

type FormFieldParams = Omit<FormField, 'id' | 'orgId' | 'fastlaneSettingId'>

interface CreateFLSettingsForPlaybookParams {
  fastlaneSettings: FastlaneSettingsParams
  formFields: FormAPIFormField[]
  siteTriggerInjectionParams?: SiteTriggerInjectionParams
}

interface CreateFLSettingsAPIParams {
  fastlaneSettingsPayload: FastlaneSettingsParams
  formFieldsPayload: FormFieldParams[]
  siteTriggerInjectionPayload: SiteTriggerInjectionParams
}

type CreateFLSettingResponse = FLSetting

const convertFormAPIFieldToFLFormField = ({
  name,
  formId,
  order,
  hidden,
  type,
}: FormAPIFormField & { order: number; formId: string }): FormFieldParams => ({
  formFieldName: name,
  formHtmlId: formId,
  displayOrder: order,
  hidden: hidden ?? false,
  fieldHtmlType: type ?? 'text',
})

const createDefaultFLTargetingCondition = (
  formUrl: string
): TargetingCondition[] => [
  {
    field: 'url',
    operator: 'isAnyOf',
    value: [formUrl],
    providerName: null,
  },
]

/**
 * API Edit Form URL
 *
 * @param params CreateFLSettingsPayload
 * @returns
 */
export const updateFLSettingsUrl = ({
  fastlaneSettings,
  formFields,
  siteTriggerInjectionParams = {
    siteTriggerType: SiteTriggerInjectionTypes.FASTLANE,
    targetingConditions: [
      createDefaultFLTargetingCondition(fastlaneSettings.detectedFormUrl),
    ],
  },
}: CreateFLSettingsForPlaybookParams) => {
  const formFieldsPayload = formFields.map((formField, i) =>
    convertFormAPIFieldToFLFormField({
      ...formField,
      order: i,
      formId: fastlaneSettings.formHtmlId,
    })
  )

  const payload: CreateFLSettingsAPIParams = {
    fastlaneSettingsPayload: fastlaneSettings,
    formFieldsPayload,
    siteTriggerInjectionPayload: siteTriggerInjectionParams,
  }

  return TargetingAPI.put<CreateFLSettingResponse>(
    `/fastlane/v2/settings/config/${fastlaneSettings.campaignId}`,
    payload
  )
}

export const UpdateFLFormUrlMutation = {
  mutationKey: 'UpdateFLFormUrl',
  mutationFn: updateFLSettingsUrl,
} as const

/**
 * API for creating Fastlane Settings for a Playbook
 * (Playbook Campaign ID required)
 *
 * @param params CreateFLSettingsPayload
 * @returns
 */
export const createFLSettingsForPlaybook = ({
  fastlaneSettings,
  formFields,
  siteTriggerInjectionParams = {
    siteTriggerType: SiteTriggerInjectionTypes.FASTLANE,
    targetingConditions: [
      createDefaultFLTargetingCondition(fastlaneSettings.detectedFormUrl),
    ],
  },
}: CreateFLSettingsForPlaybookParams) => {
  const formFieldsPayload = formFields.map((formField, i) =>
    convertFormAPIFieldToFLFormField({
      ...formField,
      order: i,
      formId: fastlaneSettings.formHtmlId,
    })
  )

  const payload: CreateFLSettingsAPIParams = {
    fastlaneSettingsPayload: fastlaneSettings,
    formFieldsPayload,
    siteTriggerInjectionPayload: siteTriggerInjectionParams,
  }

  return TargetingAPI.post<CreateFLSettingResponse>(
    '/fastlane/v2/settings/config',
    payload
  )
}

// #endregion

// #region Update Fastlane Settings for Playbook

type UpdateFLSettingsParams = FastlaneSettings

const updateFLSettings = (payload: UpdateFLSettingsParams) =>
  TargetingAPI.put<FastlaneSettings>(
    '/fastlane/v2/settings/update',
    payload
  ).then((res) => res.data)

export const UpdateFLSettingsMutation = {
  mutationKey: 'UpdateFLSettings',
  mutationFn: updateFLSettings,
} as const

// #endregion

// #region Create Playbook with Fastlane Settings

interface CreatePlaybookWithFLSettingsParams {
  playbookSettings: {
    name: string
    workspaceId: string
  }
  fastlaneSettings: Omit<FastlaneSettingsParams, 'campaignId'>
  formFields: FormAPIFormField[]
  siteTriggerInjectionParams?: SiteTriggerInjectionParams
}

const PlaybookTemplateType = {
  FASTLANE: 'FORM_SUBMISSION_FAST_LANE',
}

/**
 * API for creating Playbook with Fastlane Settings
 */
const createPlaybookWithFLSettings = async ({
  playbookSettings: { name: playbookName, workspaceId },
  fastlaneSettings,
  formFields,
  siteTriggerInjectionParams,
}: CreatePlaybookWithFLSettingsParams) => {
  const { data: playbookData } = await createPlaybook({
    name: playbookName,
    type: PlaybookTemplateType.FASTLANE,
    workspace: workspaceId,
  })

  trackEvent('Create Playbook With Basic Settings', {
    playbookId: playbookData.id,
    playbookType: PlaybookTemplateType.FASTLANE,
  })

  const campaignId = getPlaybookCampaignId(playbookData)

  if (!campaignId) {
    throw new Error('No Campaign Id')
  }

  const { data: fastlaneData } = await createFLSettingsForPlaybook({
    fastlaneSettings: {
      ...fastlaneSettings,
      campaignId,
    },
    formFields,
    siteTriggerInjectionParams,
  })

  trackEvent('Create Fastlane Settings for Playbook', {
    playbookId: playbookData.id,
    playbookType: PlaybookTemplateType.FASTLANE,
    formUrl: fastlaneSettings.detectedFormUrl,
  })

  trackEvent('Create Form Mapping with Playbook', {
    playbookId: playbookData.id,
    playbookType: PlaybookTemplateType.FASTLANE,
    noneSelected: !!fastlaneSettings,
  })

  return {
    playbookData,
    fastlaneData,
  }
}

export const CreatePlaybookWithFLSettingsMutation = {
  mutationKey: 'CreatePlaybookWithFLSettings',
  mutationFn: createPlaybookWithFLSettings,
}

// #endregion

// #region Delete Fastlane Form mappings

export type DeleteFLFormMappingsParams = FormField['id'][]

export type DeleteFLFormMappingsResponse = void

const deleteFLFormMappings = (payload: DeleteFLFormMappingsParams) =>
  TargetingAPI.post<DeleteFLFormMappingsParams>(
    '/fastlane/v2/settings/mapping/delete',
    payload
  ).then((res) => res.data)

export const DeleteFLFormMappingsMutation = {
  mutationKey: 'DeleteFLFormMappings',
  mutationFn: deleteFLFormMappings,
} as const

// #endregion

// #region Add new Fastlane form field

export type AddFLFormFieldParams = Omit<FormField, 'id' | 'orgId'>

export type AddFLFormFieldResponse = number

const addFLFormField = (formField: AddFLFormFieldParams) =>
  TargetingAPI.post<AddFLFormFieldResponse>(
    `/fastlane/v2/field/create/${formField.fastlaneSettingId}`,
    formField
  ).then((res) => res.data)

export const AddFLFormFieldMutation = {
  mutationKey: 'AddFLFormField',
  mutationFn: addFLFormField,
} as const

// #endregion

// #region Update Fastlane Form mappings

export type UpdateFLFormMappingsParams = FormFieldMapping[]

export type UpdateFLFormMappingsResponse = FormFieldMapping[]

const updateFLFormMappings = (payload: UpdateFLFormMappingsParams) =>
  TargetingAPI.post<UpdateFLFormMappingsResponse>(
    '/fastlane/v2/settings/mapping',
    payload
  ).then((res) => res.data)

export const UpdateFLFormMappingsMutation = {
  mutationKey: 'UpdateFLFormMappings',
  mutationFn: updateFLFormMappings,
} as const

// #endregion

// #region Upsert Fastlane Form field mappings

interface NewFormFields {
  newFormField: AddFLFormFieldParams
  newFormFieldMapping: Omit<FormFieldMapping, 'formFieldId'>
}

export interface UpsertFLFormMappingsParams {
  newFormFields: NewFormFields[]
  existingFormMappings: UpdateFLFormMappingsParams
  mappingsToDelete: DeleteFLFormMappingsParams
}

export type UpsertFLFormMappingsResponse = void

const upsertFLFormMappings = async ({
  newFormFields,
  existingFormMappings,
  mappingsToDelete,
}: UpsertFLFormMappingsParams) => {
  await Promise.all(
    mappingsToDelete.map((mappingId) => deleteFLFormMappings([mappingId]))
  )

  const addFLFormFields = await Promise.all(
    newFormFields.map(({ newFormField }) => addFLFormField(newFormField))
  )

  const newFormFieldMappings: FormFieldMapping[] = addFLFormFields.map(
    (formFieldId, i) => {
      const newFormFieldMapping = newFormFields[i].newFormFieldMapping

      return {
        ...newFormFieldMapping,
        formFieldId: formFieldId,
      }
    }
  )

  const updatedFormMappings = await updateFLFormMappings([
    ...existingFormMappings,
    ...newFormFieldMappings,
  ])

  return updatedFormMappings
}

export const UpsertFLFormMappingsMutation = {
  mutationKey: 'UpsertFLFormMappings',
  mutationFn: upsertFLFormMappings,
} as const
